import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { ECHTZEIT_EREIGNIS, SOCKET_PFAD } from '@mcikm-vote/shared';
import type { ZustandGeaendert } from '@mcikm-vote/shared';

import { alleAbfragen, betroffeneAbfragen } from './ereignisZuordnung';

/**
 * Anbindung des Echtzeitkanals (§22).
 *
 * Der Hook tut genau zwei Dinge: Er hält die Verbindung, und er erneuert bei jedem Anstoß die
 * betroffenen Abfragen. **Er schreibt keinen Zustand** — das Ereignis trägt keine Daten
 * (Entscheidung E18), und die verbindliche Quelle bleibt der Server (§40.3).
 *
 * Der Kanal ersetzt das Polling nicht. §22.2 verlangt es ausdrücklich als Rückfalllösung; es läuft
 * weiter, nur in größerem Abstand (siehe `zustandAbstand`). Bricht die Verbindung ab, ist die
 * Anzeige dadurch spätestens nach dem nächsten Takt wieder aktuell — ohne dass hier etwas
 * umgeschaltet werden müsste.
 */

export interface Echtzeitstand {
  /** Ob der Kanal steht. Grundlage des Verbindungshinweises (§22.2) und der Stimmabgabe. */
  verbunden: boolean;
}

/**
 * @param bereich Bestimmt, welche Abfragen ein Anlass erneuert.
 * @param aktiv Ohne Sitzung wird keine Verbindung aufgebaut — vor der Anmeldung gibt es nach §9.1
 *   ohnehin nichts anzuzeigen, was sich ändern könnte.
 */
export function useEchtzeit(bereich: 'teilnehmer' | 'admin', aktiv: boolean): Echtzeitstand {
  const queryClient = useQueryClient();
  const [verbunden, setVerbunden] = useState(false);

  useEffect(() => {
    if (!aktiv) {
      setVerbunden(false);
      return;
    }

    const socket: Socket = io({
      path: SOCKET_PFAD,
      // Same-Origin: Die Anwendung wird vom selben Server ausgeliefert (§23.2, E4). Der
      // Entwicklungsserver leitet den Pfad weiter.
      withCredentials: true,
      // §38: Bei blockierten WebSockets wird automatisch Polling verwendet. Socket.IO beginnt
      // deshalb mit HTTP-Polling und wechselt nur, wenn das Upgrade gelingt.
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
    });

    socket.on('connect', () => {
      setVerbunden(true);
      // §22.2: nach jeder Wiederverbindung eine vollständige Zustandsabfrage. Zwischen Abbruch und
      // Wiederkehr können Ereignisse verloren gegangen sein.
      for (const schluessel of alleAbfragen(bereich)) {
        void queryClient.invalidateQueries({ queryKey: schluessel });
      }
    });

    socket.on('disconnect', () => {
      setVerbunden(false);
    });

    socket.on('connect_error', () => {
      setVerbunden(false);
    });

    socket.on(ECHTZEIT_EREIGNIS.ZUSTAND_GEAENDERT, (nutzdaten: ZustandGeaendert) => {
      for (const schluessel of betroffeneAbfragen(nutzdaten.anlass, bereich)) {
        void queryClient.invalidateQueries({ queryKey: schluessel });
      }
    });

    return () => {
      socket.close();
      setVerbunden(false);
    };
  }, [aktiv, bereich, queryClient]);

  return { verbunden };
}
